/* What a plugin's standing means on screen, decided once.
 *
 * The judgement here is that **"not yet reviewed" must never look like "reviewed and safe"**. The
 * API sends three verdicts rather than a boolean precisely so a screen need not collapse them, and
 * collapsing them is the one rendering mistake that would matter: a registry showing a pending
 * plugin with no mark reads as a clean bill of health, which is the claim the whole review
 * lifecycle exists to withhold.
 *
 * So `pending` gets its own tone, its own word, and — in the table — its own explicit "not
 * reviewed" cell rather than an empty one.
 */

import type {
  Capability,
  Installation,
  Plugin,
  PluginRun,
  PluginState,
  ReviewFinding,
  ReviewVerdict,
  RunStatus,
} from '@/shared/api/generated/marketplace';

export type VerdictTone = 'ok' | 'unknown' | 'blocked';
export type StateTone = 'live' | 'progressing' | 'blocked';

const VERDICT_TONES: Record<ReviewVerdict, VerdictTone> = {
  pass: 'ok',
  /* Its own tone, never `ok`: nothing has been analysed, and a neutral mark would report the
     absence of a finding as the absence of a risk. */
  pending: 'unknown',
  fail: 'blocked',
};

const STATE_TONES: Record<PluginState, StateTone> = {
  submitted: 'progressing',
  reviewed: 'progressing',
  approved: 'progressing',
  published: 'live',
  rejected: 'blocked',
  /* **Added because `tsc` refused the map without it, which is the discipline working.**
     `satisfies Record<PluginState, StateTone>` makes a new lifecycle state a compile error rather
     than a silent fallthrough to some default — so the backend gaining `revoked` could not ship a
     screen that renders a withdrawn plugin as if nothing had changed.

     `blocked`, the same tone as `rejected`, because both are terminal and neither is installable.
     They are **not** the same fact — rejected never ran anywhere, revoked was live and was
     withdrawn — and distinguishing them is the label's job rather than the colour's. */
  revoked: 'blocked',
};

export function verdictTone(verdict: ReviewVerdict): VerdictTone {
  return VERDICT_TONES[verdict];
}

export function stateTone(state: PluginState): StateTone {
  return STATE_TONES[state];
}

/** Blocking findings are what make a verdict terminal, so they are counted separately. */
export function blockingCount(plugin: Plugin): number {
  return plugin.review_findings.filter((finding) => finding.severity === 'blocking')
    .length;
}

/**
 * Whether this tenant granted less than the plugin asked for.
 *
 * Worth surfacing rather than inferring: a plugin declaring three capabilities and installed with
 * one is working as intended, and a screen that showed only the declaration would overstate what it
 * can see every time an operator narrowed a grant deliberately.
 */
export function isNarrowed(
  plugin: Plugin,
  installation: Installation | undefined,
): boolean {
  if (installation === undefined) return false;
  return installation.granted_capabilities.length < plugin.declared_capabilities.length;
}

export function installationFor(
  plugin: Plugin,
  installations: readonly Installation[],
): Installation | undefined {
  return installations.find((installation) => installation.plugin_id === plugin.id);
}

/* The dot in a capability code is replaced rather than passed through: i18next's key separator is
 * `.`, so `marketplace.capability.dataset.schema` is a four-level lookup where the catalogue holds
 * a flat key — resolvable today by i18next's own fallback, and exactly the sort of thing that stops
 * being resolvable when somebody sets `keySeparator: false`. */
export function capabilityLabelKey(capability: Capability): string {
  return `marketplace.capability.${capability.replace('.', '_')}`;
}

/* ---------------------------------------------------------------- tabs and grouping */

export type TabId =
  'marketplace' | 'installed' | 'runs' | 'listings' | 'review' | 'activity';

export const TAB_IDS: readonly TabId[] = [
  'marketplace',
  'installed',
  'runs',
  'listings',
  'review',
  'activity',
];

/** One row per plugin *name*, carrying its versions newest-first.
 *
 * The registry lists a row per (name, version), so a plugin with four versions is four rows and a
 * reader cannot see at a glance which one is current. Grouping is the design's answer, and the
 * ordering is by semver descending rather than by submission time: after a revocation the newest
 * submission is not the greatest version. The backend orders its own version list the same way and
 * for the same reason. */
export interface PluginGroup {
  readonly name: string;
  readonly versions: readonly Plugin[];
}

function semverParts(version: string): readonly number[] {
  return version.split('.').map((part) => Number.parseInt(part, 10) || 0);
}

export function compareVersionsDesc(a: string, b: string): number {
  const left = semverParts(a);
  const right = semverParts(b);
  const width = Math.max(left.length, right.length);
  for (let index = 0; index < width; index += 1) {
    const difference = (right[index] ?? 0) - (left[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return b.localeCompare(a);
}

export function groupByName(plugins: readonly Plugin[]): readonly PluginGroup[] {
  const groups = new Map<string, Plugin[]>();
  for (const plugin of plugins) {
    const existing = groups.get(plugin.name);
    if (existing === undefined) groups.set(plugin.name, [plugin]);
    else existing.push(plugin);
  }
  return [...groups.entries()]
    .map(([name, versions]) => ({
      name,
      versions: [...versions].sort((a, b) => compareVersionsDesc(a.version, b.version)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Whether an installed version has a newer *published* sibling to move to.
 *
 * Deliberately not "the newest version": a submitted, rejected or revoked sibling is not
 * installable, and offering it as an upgrade target would produce a 409 from a control that looked
 * available. */
export function upgradeTargetFor(
  installed: Plugin,
  plugins: readonly Plugin[],
): Plugin | undefined {
  return plugins
    .filter(
      (candidate) =>
        candidate.name === installed.name &&
        candidate.state === 'published' &&
        candidate.id !== installed.id &&
        compareVersionsDesc(candidate.version, installed.version) < 0,
    )
    .sort((a, b) => compareVersionsDesc(a.version, b.version))[0];
}

/* ---------------------------------------------------------------- runs */

export type RunTone = 'ok' | 'blocked';

const RUN_TONES = {
  succeeded: 'ok',
  /* Refused and failed share a tone and are **not** the same fact: refused means the platform
     declined before executing anything — revoked, disabled, not installed — and failed means the
     sandbox ran and could not finish. The label carries that difference; the colour carries only
     "this produced nothing", which is what a reader scanning a list needs. */
  refused: 'blocked',
  failed: 'blocked',
} satisfies Record<RunStatus, RunTone>;

export function runTone(status: RunStatus): RunTone {
  return RUN_TONES[status];
}

/** A run whose plugin saw less than the tenant granted it.
 *
 * The backend omits a capability slice when the *actor* lacks the underlying permission, and the
 * run still succeeds — so a thin result is a successful run over less metadata than expected, and
 * a screen showing only "succeeded" would leave a reader puzzling over a short finding list.
 * Compared by length because the substrate refuses `used ⊄ granted`, so a shorter used-list is
 * exactly the narrowing rather than a different set. */
export function isThinRun(run: PluginRun): boolean {
  return run.capabilities_used.length < run.granted_capabilities.length;
}

/* ---------------------------------------------------------------- listing lifecycle */

export type ListingStage = 'submitted' | 'reviewed' | 'published';
export type StageState = 'done' | 'current' | 'pending' | 'failed';

/** Where a listing stands on the submit → review → publish arc, for a step strip.
 *
 * `rejected` fails at the review step rather than advancing, and `revoked` shows the arc complete
 * because it *was* — the withdrawal is a terminal state the caller renders beside the strip, not a
 * fourth step. Collapsing them would lose the difference between a plugin that never passed review
 * and one that was published and later withdrawn. */
export function stageStates(
  state: PluginState,
  verdict: ReviewVerdict,
): Record<ListingStage, StageState> {
  if (state === 'rejected') {
    return { submitted: 'done', reviewed: 'failed', published: 'pending' };
  }
  if (state === 'published' || state === 'revoked') {
    return { submitted: 'done', reviewed: 'done', published: 'done' };
  }
  if (verdict === 'pass') {
    return { submitted: 'done', reviewed: 'done', published: 'current' };
  }
  return { submitted: 'done', reviewed: 'current', published: 'pending' };
}

export function isTerminal(state: PluginState): boolean {
  return state === 'rejected' || state === 'revoked';
}

export function blockingFindings(plugin: Plugin): readonly ReviewFinding[] {
  return plugin.review_findings.filter((finding) => finding.severity === 'blocking');
}

export function advisoryFindings(plugin: Plugin): readonly ReviewFinding[] {
  return plugin.review_findings.filter((finding) => finding.severity === 'advisory');
}

/* ---------------------------------------------------------------- offering actions */

/** Why an action cannot be offered, as a copy key, or `null` when it can.
 *
 * A reason rather than a boolean, because the design shows a disabled control with its reason
 * rather than hiding it — "a revoked plugin cannot be run" is the platform explaining itself, where
 * an absent button reads as an oversight. Permission is checked separately by the caller and
 * deliberately not folded in: state and permission are different refusals, and collapsing them
 * would tell somebody who merely lacks `run` that the plugin is revoked. */
export function runBlockedReason(
  plugin: Plugin | undefined,
  installation: Installation | undefined,
): string | null {
  if (plugin?.state === 'revoked') return 'marketplace.blocked.run.revoked';
  if (plugin !== undefined && !plugin.runnable)
    return 'marketplace.blocked.run.unpublished';
  if (installation === undefined) return 'marketplace.blocked.run.notInstalled';
  if (!installation.enabled) return 'marketplace.blocked.run.disabled';
  return null;
}

export function installBlockedReason(plugin: Plugin): string | null {
  if (plugin.state === 'revoked') return 'marketplace.blocked.install.revoked';
  if (plugin.state !== 'published') return 'marketplace.blocked.install.unpublished';
  return null;
}

/** The capabilities a grant may contain: the plugin's declaration, never more.
 *
 * The server refuses an undeclared capability with a 422 naming the offending codes, because the
 * security review never considered them. Offering only the declared set is what stops a reader
 * meeting that refusal at all. */
export function grantableCapabilities(plugin: Plugin): readonly Capability[] {
  return plugin.declared_capabilities;
}

/* ---------------------------------------------------------------- searching the registry */

/** Whether one submission answers a search term.
 *
 * Name and artifact hash, and nothing else. The name is what somebody remembers; the hash is what
 * they have in front of them — a review note, an audit entry, or the truncated twelve characters
 * this screen prints in every row, which is why the hash matches as a *prefix* rather than as a
 * substring. Matching the version too would be a trap: `1.0.0` appears in most registries and
 * would return everything while looking like a filter that worked.
 *
 * Case-insensitive on the name because a plugin name is typed from memory, and case-insensitive on
 * the hash because hex is copied from wherever it was rendered. */
export function matchesQuery(plugin: Plugin, query: string): boolean {
  const term = query.trim().toLowerCase();
  if (term === '') return true;
  return (
    plugin.name.toLowerCase().includes(term) ||
    plugin.artifact_hash.toLowerCase().startsWith(term)
  );
}
